<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { AdminPersonRecord } from '../../lib/types';
  import ChipGroup, { type ChipOption } from './ui/ChipGroup.svelte';
  import IdentityBadge from './ui/IdentityBadge.svelte';
  import StatusBadge from './ui/StatusBadge.svelte';
  import SurfaceCard from './ui/SurfaceCard.svelte';
  import PersonEditorModal from './PersonEditorModal.svelte';
  import ConfirmDialog from './ConfirmDialog.svelte';
  import { deleteAdminPerson } from '../../lib/convexApi';

  export let people: AdminPersonRecord[] = [];
  export let loading = false;
  export let error: string | null = null;

  const dispatch = createEventDispatcher<{ refresh: void }>();

  type SortKey = 'activity' | 'name' | 'tickets';
  type SortDirection = 'asc' | 'desc';

  const identityOptions: ChipOption[] = [
    { value: 'all', label: 'All' },
    { value: 'linked', label: 'Linked' },
    { value: 'email_only', label: 'Email captured' },
    { value: 'name_only', label: 'Needs enrichment' }
  ];

  const participationOptions: ChipOption[] = [
    { value: 'all', label: 'All' },
    { value: '1+', label: '1+ events' },
    { value: '2+', label: '2+ events' },
    { value: '3+', label: '3+ events' }
  ];

  const spendOptions: ChipOption[] = [
    { value: 'all', label: 'All' },
    { value: 'has', label: 'Has spend' },
    { value: 'none', label: 'No spend' }
  ];

  let identityFilter: string = 'all';
  let participationFilter: string = 'all';
  let spendFilter: string = 'all';
  let searchTerm = '';
  let sortKey: SortKey = 'activity';
  let sortDirection: SortDirection = 'desc';
  let selectedPerson: AdminPersonRecord | null = null;
  let showPersonModal = false;
  let personForEdit: AdminPersonRecord | null = null;
  let showDeleteModal = false;
  let personForDelete: AdminPersonRecord | null = null;
  let deleteBusy = false;
  let deleteError: string | null = null;

  const ticketStatusLabel: Record<string, string> = {
    issued: 'Issued',
    redeemed: 'Redeemed',
    pending: 'Pending',
    invited: 'Invited',
    cancelled: 'Cancelled'
  };

  function normalizeStatus(status: string | null | undefined): 'issued' | 'redeemed' | 'pending' | 'invited' | 'cancelled' {
    switch (status) {
      case 'redeemed':
        return 'redeemed';
      case 'pending':
        return 'pending';
      case 'invited':
        return 'invited';
      case 'cancelled':
        return 'cancelled';
      case 'issued':
      default:
        return 'issued';
    }
  }

  $: filteredPeople = applyFilters(people);
  $: sortedPeople = applySort(filteredPeople);
  $: totalPeople = people.length;
  $: linkedCount = people.filter(person => person.identityStatus === 'linked').length;
  $: enrichmentNeeded = people.filter(person => person.identityStatus === 'name_only').length;
  $: if (sortedPeople.length === 0) {
    selectedPerson = null;
  } else if (!selectedPerson && !showPersonModal && !showDeleteModal) {
    selectedPerson = sortedPeople[0];
  }

  function applyFilters(records: AdminPersonRecord[]): AdminPersonRecord[] {
    const term = searchTerm.trim().toLowerCase();
    return records.filter(record => {
      if (identityFilter !== 'all' && record.identityStatus !== identityFilter) return false;

      if (participationFilter !== 'all') {
        const min = Number(participationFilter.replace('+', ''));
        if (record.eventCount < min) return false;
      }

      if (spendFilter === 'has' && record.extrasSpend <= 0) return false;
      if (spendFilter === 'none' && record.extrasSpend > 0) return false;

      if (!term) return true;

      const haystacks = [
        record.person.name,
        record.person.email ?? '',
        record.person.phone ?? '',
        ...record.tickets.flatMap(ticket => [ticket.code, ticket.eventName])
      ].map(value => value.toLowerCase());

      return haystacks.some(value => value.includes(term));
    });
  }

  function applySort(records: AdminPersonRecord[]): AdminPersonRecord[] {
    const sorted = [...records];
    sorted.sort((a, b) => {
      if (sortKey === 'name') {
        const aName = a.person.name.toLowerCase();
        const bName = b.person.name.toLowerCase();
        return sortDirection === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
      }
      if (sortKey === 'tickets') {
        return sortDirection === 'asc' ? a.ticketCount - b.ticketCount : b.ticketCount - a.ticketCount;
      }
      const aTs = toTimestamp(a.lastActivity);
      const bTs = toTimestamp(b.lastActivity);
      return sortDirection === 'asc' ? aTs - bTs : bTs - aTs;
    });
    return sorted;
  }

  function toTimestamp(value: string | null): number {
    if (!value) return 0;
    const ts = Date.parse(value);
    return Number.isNaN(ts) ? 0 : ts;
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      sortKey = key;
      sortDirection = key === 'name' ? 'asc' : 'desc';
    }
  }

  function openProfile(person: AdminPersonRecord) {
    selectedPerson = person;
  }

  function openEdit(person: AdminPersonRecord) {
    selectedPerson = person;
    personForEdit = person;
    showPersonModal = true;
  }

  function closeEdit() {
    showPersonModal = false;
    personForEdit = null;
  }

  function handlePersonSaved() {
    closeEdit();
    dispatch('refresh');
  }

  function openDelete(person: AdminPersonRecord) {
    selectedPerson = person;
    personForDelete = person;
    deleteError = null;
    deleteBusy = false;
    showDeleteModal = true;
  }

  function closeDelete() {
    showDeleteModal = false;
    personForDelete = null;
    deleteError = null;
    deleteBusy = false;
  }

  async function confirmDelete() {
    if (!personForDelete) return;
    deleteBusy = true;
    deleteError = null;
    try {
      await deleteAdminPerson(personForDelete.person.id);
      if (selectedPerson?.person.id === personForDelete.person.id) {
        selectedPerson = null;
      }
      closeDelete();
      dispatch('refresh');
    } catch (error) {
      deleteError = error instanceof Error ? error.message : 'Unable to delete person';
    } finally {
      deleteBusy = false;
    }
  }

  function formatCurrency(amount: number): string {
    if (!Number.isFinite(amount) || amount <= 0) return '—';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount);
    } catch {
      return `€${amount.toFixed(2)}`;
    }
  }

  function formatDate(value: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return `${date.toLocaleDateString()} · ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  function openDetail(record: AdminPersonRecord) {
    selectedPerson = record;
  }

  function closeDetail() {
    selectedPerson = null;
  }

  function requestRefresh() {
    dispatch('refresh');
  }
</script>

<section class="people-directory">
  <header class="directory-header">
    <div>
      <h2>People Directory</h2>
      <p class="subtitle">Understand every guest across events and keep identities clean.</p>
      <div class="header-stats">
        <span class="chip">{totalPeople} people</span>
        <span class="chip">{linkedCount} linked</span>
        <span class="chip warning">{enrichmentNeeded} need enrichment</span>
      </div>
    </div>
    <button type="button" class="ghost" on:click={requestRefresh} disabled={loading}>
      {loading ? 'Refreshing…' : 'Refresh'}
    </button>
  </header>

  {#if loading && people.length === 0}
    <SurfaceCard>Loading people…</SurfaceCard>
  {:else if error}
    <SurfaceCard><p class="error-text">{error}</p></SurfaceCard>
  {:else if people.length === 0}
    <SurfaceCard>No people yet. Imports and sign-ins will populate this ledger.</SurfaceCard>
  {:else}
    <div class="people-grid" class:compact={!selectedPerson}>
      <div class="main-column">
        <SurfaceCard>
          <div class="filter-stack">
            <div class="search">
              <input
                type="search"
                placeholder="Search name, email, phone, or event"
                bind:value={searchTerm}
              />
            </div>
            <div class="chip-row">
              <div class="chip-block">
                <span>Identity</span>
                <ChipGroup options={identityOptions} bind:value={identityFilter} ariaLabel="Identity filter" />
              </div>
              <div class="chip-block">
                <span>Participation</span>
                <ChipGroup options={participationOptions} bind:value={participationFilter} ariaLabel="Participation filter" />
              </div>
              <div class="chip-block">
                <span>Extras spend</span>
                <ChipGroup options={spendOptions} bind:value={spendFilter} ariaLabel="Spend filter" />
              </div>
            </div>
          </div>
        </SurfaceCard>

        <SurfaceCard>
          <table class="people-table">
            <thead>
              <tr>
                <th class="sortable" on:click={() => toggleSort('name')}>
                  Person
                  {#if sortKey === 'name'}
                    <span class="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  {/if}
                </th>
                <th>Contact</th>
                <th>Identity</th>
                <th class="sortable" on:click={() => toggleSort('tickets')}>
                  Events / tickets
                  {#if sortKey === 'tickets'}
                    <span class="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  {/if}
                </th>
                <th>Extras</th>
                <th class="sortable" on:click={() => toggleSort('activity')}>
                  Last activity
                  {#if sortKey === 'activity'}
                    <span class="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  {/if}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each sortedPeople as record (record.person.id)}
                <tr>
                  <td>
                    <div class="person-name">{record.person.name}</div>
                    <div class="muted">Created {formatDate(record.person.createdAt ? new Date(record.person.createdAt).toISOString() : null)}</div>
                  </td>
                  <td>
                    {#if record.person.email}
                      <div>{record.person.email}</div>
                    {/if}
                    {#if record.person.phone}
                      <div class="muted">{record.person.phone}</div>
                    {/if}
                    {#if !record.person.email && !record.person.phone}
                      <div class="muted warning">No contact info</div>
                    {/if}
                  </td>
                  <td>
                    <IdentityBadge status={record.identityStatus} />
                    {#if record.person.externalId}
                      <div class="muted">Clerk: {record.person.externalId}</div>
                    {/if}
                  </td>
                  <td>
                    <div class="ticket-count">{record.eventCount} events · {record.ticketCount} tickets</div>
                    {#if record.tickets.length}
                      <div class="ticket-tags">
                        {#each record.tickets.slice(0, 3) as ticket}
                          <StatusBadge
                            status={normalizeStatus(ticket.status)}
                            label={ticket.eventName}
                          />
                        {/each}
                        {#if record.tickets.length > 3}
                          <span class="ticket-tag more">+{record.tickets.length - 3}</span>
                        {/if}
                      </div>
                    {/if}
                  </td>
                  <td>{formatCurrency(record.extrasSpend)}</td>
                  <td>{formatDate(record.lastActivity)}</td>
                  <td class="actions">
                    <button type="button" class="ghost" on:click={() => openProfile(record)}>Profile</button>
                    <button type="button" class="ghost" on:click={() => openEdit(record)}>Edit</button>
                    <button type="button" class="ghost danger" on:click={() => openDelete(record)}>Delete</button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </SurfaceCard>
      </div>

      {#if selectedPerson}
        <SurfaceCard class="detail-card">
          <header class="detail-header">
            <div>
              <h3>{selectedPerson.person.name}</h3>
              <IdentityBadge status={selectedPerson.identityStatus} />
            </div>
            <button type="button" class="ghost" on:click={closeDetail}>Close</button>
          </header>

          <div class="detail-section">
            <h4>Contact</h4>
            <ul>
              <li>Email: {selectedPerson.person.email ?? '—'}</li>
              <li>Phone: {selectedPerson.person.phone ?? '—'}</li>
              <li>Clerk ID: {selectedPerson.person.externalId ?? '—'}</li>
            </ul>
          </div>

          <div class="detail-section">
            <h4>Metrics</h4>
            <div class="stat-grid">
              <div><strong>{selectedPerson.eventCount}</strong><span>Events</span></div>
              <div><strong>{selectedPerson.ticketCount}</strong><span>Tickets</span></div>
              <div><strong>{formatCurrency(selectedPerson.extrasSpend)}</strong><span>Extras spend</span></div>
              <div><strong>{formatDate(selectedPerson.lastActivity)}</strong><span>Last activity</span></div>
            </div>
          </div>

          <div class="detail-section">
            <h4>Tickets</h4>
            {#if selectedPerson.tickets.length === 0}
              <p class="muted">No tickets yet.</p>
            {:else}
              <ul class="ticket-list">
                {#each selectedPerson.tickets as ticket}
                  <li>
                    <div class="ticket-header">
                      <span class="ticket-event">{ticket.eventName}</span>
                      <span class="ticket-code">{ticket.code}</span>
                    </div>
                    <div class="ticket-meta">
                      <StatusBadge status={normalizeStatus(ticket.status)} label={ticketStatusLabel[ticket.status ?? ''] ?? ticket.status ?? 'Issued'} />
                      {#if ticket.redeemedAt}
                        <span class="muted">Redeemed {formatDate(ticket.redeemedAt)}</span>
                      {/if}
                    </div>
                    {#if ticket.extras.length}
                      <ul class="ticket-extras">
                        {#each ticket.extras as extra}
                          <li>{extra.sku} ×{extra.quantity}</li>
                        {/each}
                      </ul>
                    {/if}
                  </li>
                {/each}
              </ul>
            {/if}
          </div>

          <div class="detail-actions">
            <button type="button" on:click={() => selectedPerson && openEdit(selectedPerson)}>Edit person</button>
            <button type="button" class="ghost" on:click={() => selectedPerson && openDelete(selectedPerson)}>Delete person</button>
          </div>
        </SurfaceCard>
      {/if}
    </div>
  {/if}
</section>

<PersonEditorModal
  open={showPersonModal}
  person={personForEdit}
  on:close={closeEdit}
  on:saved={handlePersonSaved}
/>

<ConfirmDialog
  open={showDeleteModal}
  title="Delete person"
  message={personForDelete ? `Deleting ${personForDelete.person.name} will remove their profile. Remove their tickets first.` : ''}
  confirmLabel="Delete"
  busy={deleteBusy}
  error={deleteError}
  on:close={closeDelete}
  on:confirm={confirmDelete}
/>

<style>
  .people-directory {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }

  .directory-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .directory-header h2 {
    margin: 0;
    font-size: 1.6rem;
    font-weight: 600;
  }

  .subtitle {
    margin: 0.35rem 0 0;
    color: var(--color-text-muted);
  }

  .header-stats {
    margin-top: var(--space-3);
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .chip {
    padding: 0.3rem 0.75rem;
    border-radius: var(--radius-md);
    background: rgba(255, 255, 255, 0.08);
    font-size: 0.75rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .chip.warning {
    background: rgba(255, 206, 115, 0.24);
    color: #ffce73;
  }

  button.ghost {
    border: none;
    background: rgba(255, 255, 255, 0.12);
    color: inherit;
    padding: 0.55rem 1rem;
    border-radius: var(--radius-lg);
    cursor: pointer;
  }

  .people-grid {
    display: grid;
    gap: var(--space-5);
    grid-template-columns: minmax(0, 1fr) 360px;
  }

  .people-grid.compact {
    grid-template-columns: minmax(0, 1fr);
  }

  .main-column {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .filter-stack {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .search input {
    width: 100%;
    padding: 0.65rem 0.9rem;
    border-radius: var(--radius-lg);
    border: none;
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
  }

  .chip-row {
    display: grid;
    gap: var(--space-3);
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  .chip-block {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    font-size: 0.8rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  .people-table {
    width: 100%;
    border-collapse: collapse;
  }

  .people-table thead {
    background: rgba(255, 255, 255, 0.04);
    font-size: 0.75rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .people-table th,
  .people-table td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  .people-table th.sortable {
    cursor: pointer;
  }

  .people-table tbody tr:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  .person-name {
    font-weight: 600;
  }

  .muted {
    color: var(--color-text-muted);
    font-size: 0.85rem;
  }

  .muted.warning {
    color: #ffce73;
  }

  .ticket-count {
    font-weight: 500;
    margin-bottom: 0.35rem;
  }

  .ticket-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
  }

  .ticket-tag.more {
    padding: 0.2rem 0.55rem;
    border-radius: var(--radius-md);
    background: rgba(255, 255, 255, 0.12);
    font-size: 0.7rem;
  }

  .actions {
    display: flex;
    gap: 0.3rem;
  }

  .actions .ghost {
    padding: 0.4rem 0.8rem;
  }

  .actions .danger {
    color: #ff9a9a;
  }

:global(.detail-card) {
    position: sticky;
    top: var(--space-5);
    max-height: calc(100vh - 2 * var(--space-5));
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-3);
  }

  .detail-section h4 {
    margin: 0 0 0.35rem;
    font-size: 0.95rem;
  }

  .detail-section ul {
    margin: 0;
    padding-left: 1rem;
  }

  .stat-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--space-3);
  }

  .stat-grid div {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .ticket-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: var(--space-3);
  }

  .ticket-header {
    display: flex;
    justify-content: space-between;
  }

  .ticket-event {
    font-weight: 600;
  }

  .ticket-code {
    font-family: var(--font-mono, 'DM Mono', monospace);
    letter-spacing: 0.08em;
  }

  .ticket-meta {
    display: flex;
    gap: var(--space-2);
    align-items: center;
  }

  .ticket-extras {
    margin: 0.4rem 0 0;
    padding-left: 1rem;
    font-size: 0.85rem;
  }

  .detail-actions {
    display: flex;
    gap: var(--space-3);
  }

  .detail-actions button {
    border: none;
    border-radius: var(--radius-lg);
    padding: 0.6rem 1rem;
    cursor: pointer;
    font-weight: 600;
  }

  .detail-actions button:not(.ghost) {
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.6), rgba(178, 127, 244, 0.4));
    color: #050608;
  }

  .error-text {
    color: #ff9a9a;
  }

  @media (max-width: 1180px) {
    .people-grid {
      grid-template-columns: 1fr;
    }

    :global(.detail-card) {
      position: static;
      max-height: none;
    }
  }

  @media (max-width: 720px) {
    .directory-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .people-table thead {
      display: none;
    }

    .people-table tbody tr {
      display: grid;
      gap: var(--space-3);
      padding: var(--space-3);
    }

    .people-table td,
    .people-table th {
      padding: 0;
      border: none;
    }
  }
</style>
